'use client';

import { ChevronDown } from 'lucide-react';
import { Input } from '@/core/presentation/components/ui/Input';
import { Label } from '@/core/presentation/components/ui/Label';
import { PLAN_BILLING_PERIOD_LABELS, type PlanBillingPeriod } from '@/core/domain';
import {
  PLAN_ADMIN_VARIANT_PRICE_HELP,
  PLAN_ADMIN_VARIANT_PRICE_LABEL,
} from '@/features/plans/presentation/composables/constants/plan-admin.constants';
import {
  STRIPE_PLAN_EXPLAINER,
  STRIPE_PRICE_TEST_HINT,
  STRIPE_PRODUCTS_URL,
} from '@/features/plans/presentation/composables/constants/stripe-plan-hints';
import type { PlanVariantFormSlice } from '@/features/plans/presentation/admin/utils/plan-form';
import { cn } from '@/lib/utils';

export interface PlanBillingVariantFieldsProps {
  period: PlanBillingPeriod;
  value: PlanVariantFormSlice;
  onChange: (next: PlanVariantFormSlice) => void;
  disabled?: boolean;
  defaultOpen?: boolean;
}

export function PlanBillingVariantFields({
  period,
  value,
  onChange,
  disabled = false,
  defaultOpen = false,
}: PlanBillingVariantFieldsProps) {
  const label = PLAN_BILLING_PERIOD_LABELS[period];
  const idBase = `plan-variant-${period}`;

  return (
    <details
      className="group rounded-lg border border-admin-border bg-admin-bg-block overflow-hidden"
      open={defaultOpen}
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5',
          'text-sm font-semibold text-admin-text hover:bg-white/60',
          '[&::-webkit-details-marker]:hidden'
        )}
      >
        <span>{label}</span>
        <ChevronDown
          className="h-4 w-4 shrink-0 text-admin-text-secondary transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>

      <div className="space-y-3 border-t border-admin-border px-3 py-3">
        <div>
          <Label htmlFor={`${idBase}-price`} className="text-admin-text">
            {PLAN_ADMIN_VARIANT_PRICE_LABEL}
          </Label>
          <p className="mt-0.5 text-xs text-admin-text-secondary">{PLAN_ADMIN_VARIANT_PRICE_HELP}</p>
          <Input
            id={`${idBase}-price`}
            inputMode="decimal"
            value={value.priceText}
            onChange={(e) => onChange({ ...value, priceText: e.target.value })}
            className="mt-1.5 border-admin-border"
            variant="light"
            disabled={disabled}
            placeholder="79,90"
          />
        </div>

        <div className="space-y-3 rounded-md border border-admin-border/80 bg-white/50 px-2.5 py-2.5">
          <p className="text-xs text-admin-text-secondary leading-relaxed">{STRIPE_PLAN_EXPLAINER}</p>
          <p className="text-xs">
            <a
              href={STRIPE_PRODUCTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-admin-cyan-dark underline font-medium"
            >
              Abrir produtos no Stripe
            </a>
          </p>
          <div>
            <Label htmlFor={`${idBase}-stripe-id`} className="text-admin-text">
              ID do Price (Stripe)
            </Label>
            <Input
              id={`${idBase}-stripe-id`}
              value={value.stripePriceId}
              onChange={(e) => onChange({ ...value, stripePriceId: e.target.value })}
              className="mt-1 border-admin-border font-mono text-sm"
              variant="light"
              disabled={disabled}
              placeholder="price_1AbCdEfGhIjKlMn"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor={`${idBase}-stripe-id-test`} className="text-admin-text">
              ID do Price — modo teste (opcional)
            </Label>
            <p className="mt-0.5 text-xs text-admin-text-secondary">{STRIPE_PRICE_TEST_HINT}</p>
            <Input
              id={`${idBase}-stripe-id-test`}
              value={value.stripePriceIdTest}
              onChange={(e) => onChange({ ...value, stripePriceIdTest: e.target.value })}
              className="mt-1 border-admin-border font-mono text-sm"
              variant="light"
              disabled={disabled}
              placeholder="price_… (teste)"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor={`${idBase}-stripe-lookup`} className="text-admin-text">
              Lookup key (opcional)
            </Label>
            <Input
              id={`${idBase}-stripe-lookup`}
              value={value.stripeLookupKey}
              onChange={(e) => onChange({ ...value, stripeLookupKey: e.target.value })}
              className="mt-1 border-admin-border font-mono text-sm"
              variant="light"
              disabled={disabled}
              placeholder="ex.: premium_mensal"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor={`${idBase}-stripe-lookup-test`} className="text-admin-text">
              Lookup key — teste (opcional)
            </Label>
            <Input
              id={`${idBase}-stripe-lookup-test`}
              value={value.stripeLookupKeyTest}
              onChange={(e) => onChange({ ...value, stripeLookupKeyTest: e.target.value })}
              className="mt-1 border-admin-border font-mono text-sm"
              variant="light"
              disabled={disabled}
              autoComplete="off"
            />
          </div>
        </div>
      </div>
    </details>
  );
}
